import { inject, Injectable } from '@angular/core';
import { firstValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ChunkContext } from '../../interfaces/chunk/chunk-context.interface';
import { ExecJob, ExecPlan, TriggerRoutineRequest } from '../../interfaces';
import { AppStateService } from '../app-state/app-state.service';
import { ExecutionPlanService } from '../execution-plan/execution-plan.service';
import { ObjectStorageFileService } from '../object-storage/object-storage-file.service';
import { CacheService } from '../cache/cache.service';
import { FileDataService } from '../file-data/file-data.service';
import { MessageService } from '../message/message.service';
import { RELOAD_NOTEBOOK_FILES } from '../../constants/general.constants';
import { GeneralHelpers } from '../../helpers/general.helper';
import { normalizeReviewFileNames } from '../../helpers/review-files.helper';
import { PYTHON_DIRECTORIES } from '../python/interfaces/python.interface';

/** Above this size, skip notebook cache sync to avoid a second full-file buffer (SCRUM-333). */
const MAX_BYTES_FOR_CACHE_SYNC = 32 * 1024 * 1024;


/**
 * RoutineService — routine / job helpers callable from Python and JavaScript steps.
 *
 * SCRUM-259: Where nc-server exposes an HTTP API, call it from the browser; for
 * headless-only job control, warn and no-op so workflows stay debuggable.
 */
@Injectable({
  providedIn: 'root',
})
export class RoutineService {
  private readonly appState = inject(AppStateService);
  private readonly executionPlanService = inject(ExecutionPlanService);
  private readonly objectStorageFileService = inject(ObjectStorageFileService);
  private readonly cacheService = inject(CacheService);
  private readonly fileDataService = inject(FileDataService);
  private readonly messageService = inject(MessageService);

  private pyodideFS: any = null;

  /**
   * Set Pyodide FS reference so downloaded files are written to the in-memory
   * filesystem for immediate access within the same step (matches BoxService).
   */
  setPyodideFS(fs: any): void {
    this.pyodideFS = fs;
  }

  /**
   * Request a retry of the current job — halts execution immediately.
   *
   * Parity with enoch's RoutineService.requestRetry (which sets
   * globalThis._retryRequested and throws RetryRequestedError): this MUST stop
   * the step rather than warn-and-continue, otherwise code after requestRetry()
   * keeps running (SCRUM-718). The flag lets the step executor render a clean
   * "Retry requested: <msg>" outcome instead of a red execution error — a
   * designed control-halt is not a crash. See abortJob for why a global flag is
   * used (survives the Pyodide marshaling boundary; a thrown class identity does
   * not).
   * args: [message?: string]
   */
  requestRetry(args: any[], _context?: ChunkContext, _runtime?: string): never {
    const message =
      Array.isArray(args) && typeof args[0] === 'string' && args[0].trim()
        ? args[0].trim()
        : 'Retry requested';
    (globalThis as any)._retryRequested = message;
    throw new Error(message);
  }

  /**
   * Abort the current job — halts execution immediately.
   *
   * Parity with enoch's RoutineService.abortJob (which sets the job status to
   * Aborted and throws JobAbortedError): abortJob MUST stop the step. The old
   * warn-and-continue stub let code after abortJob() keep running — e.g. a
   * diagnose step that called abortJob() still fell through to the API call
   * after it (SCRUM-718). Throwing halts the step exactly as headless execution
   * does.
   *
   * Before throwing we flag the controlled abort on `globalThis._abortRequested`
   * (mirroring enoch, which detects the abort via this same global). The step
   * executor reads the flag to render a clean "Job aborted: <msg>" outcome
   * instead of a red "Execution error: ehq.abortJob() failed" — a designed abort
   * is not a crash. The flag is used rather than a JobAbortedError subclass
   * because a thrown class identity does not reliably survive the Pyodide
   * marshaling boundary, whereas the global does (this is why enoch uses it too).
   * args: [message?: string]
   */
  abortJob(args: any[], _context?: ChunkContext, _runtime?: string): never {
    const message =
      Array.isArray(args) && typeof args[0] === 'string' && args[0].trim()
        ? args[0].trim()
        : 'Job aborted';
    (globalThis as any)._abortRequested = message;
    throw new Error(message);
  }

  /**
   * Request operator review of the current job — halts execution immediately.
   *
   * Parity with enoch's RoutineService.requestReview (which sets
   * globalThis._reviewRequested and throws ReviewRequestedError). Like
   * abortJob/requestRetry, throwing halts the step exactly as headless execution
   * does, and the flag lets the executor render a clean "Review requested: <msg>"
   * outcome instead of a red execution error (SCRUM-718).
   *
   * The optional fileNames argument (EHQ-134) is accepted and validated here so
   * an author gets the same feedback in the browser as headless, but it has no
   * effect in preview: resolving names to files needs a persisted job, and a
   * preview run never creates one. Saying so in the step output is better than
   * silently swallowing it and leaving the author to wonder.
   * args: [message?: string, fileNames?: string[] | string]
   */
  requestReview(args: any[], context?: ChunkContext, _runtime?: string): never {
    const message =
      Array.isArray(args) && typeof args[0] === 'string' && args[0].trim()
        ? args[0].trim()
        : 'Review requested';

    // Flag the controlled halt BEFORE looking at the file list, mirroring
    // enoch. Normalization touches caller-supplied input — a toJs() or an
    // iterator — which can throw on its own. Were that to happen before the
    // flag is set, the executor would see an unflagged throw and render a red
    // "requestReview() failed" error instead of the clean "Review requested"
    // outcome, which is the exact failure the file list must never cause.
    (globalThis as any)._reviewRequested = message;

    let fileNames: string[] | undefined;
    try {
      fileNames = normalizeReviewFileNames(Array.isArray(args) ? args[1] : undefined);
    } catch (e) {
      // The review is already flagged, so drop the unusable list and let the
      // review proceed without it.
      fileNames = undefined;
      context?.addMessage(
        `requestReview: ignoring an unusable file list (${e}); the review will offer all of the job's files.`,
        'warning',
      );
    }

    if (fileNames && context) {
      context.addMessage(
        `requestReview: ${fileNames.length} review ${
          fileNames.length === 1 ? 'file' : 'files'
        } named (${fileNames.join(', ')}). These are recorded only when the workflow runs as a job — in preview they are ignored.`,
        'warning',
      );
    }

    throw new Error(message);
  }

  /**
   * Get routine metadata by name.
   */
  async getRoutineInfo(args: any[], context?: ChunkContext): Promise<ExecPlan | null> {
    const projectId = this.appState.projectId();
    if (!projectId) {
      const msg = 'getRoutineInfo: no active project';
      if (context) {
        context.addMessage(msg, 'danger');
      }
      throw new Error(msg);
    }
    if (!args?.length || typeof args[0] !== 'string' || !args[0].trim()) {
      const msg = 'getRoutineInfo requires a routine name (string)';
      if (context) {
        context.addMessage(msg, 'danger');
        context.setChunkResultStatus('error');
      }
      throw new Error(msg);
    }
    const name = args[0].trim();
    try {
      return await firstValueFrom(
        this.executionPlanService.getPlanByRoutineName(projectId, name).pipe(
          catchError((err: { status?: number }) => {
            if (err?.status === 404) {
              return of(null);
            }
            throw err;
          })
        )
      );
    } catch (error: unknown) {
      const msg = `Error in getRoutineInfo: ${error instanceof Error ? error.message : String(error)}`;
      if (context) {
        context.addMessage(msg, 'danger');
        context.setChunkResultStatus('error');
      }
      throw error;
    }
  }

  /**
   * Pause or resume a routine by numeric routine id.
   * args: [routineId: number, paused: boolean]
   */
  async setRoutinePaused(args: any[], context?: ChunkContext): Promise<void> {
    const projectId = this.appState.projectId();
    if (!projectId) {
      const msg = 'setRoutinePaused: no active project';
      if (context) {
        context.addMessage(msg, 'danger');
      }
      throw new Error(msg);
    }
    if (args.length < 2 || typeof args[0] !== 'number' || typeof args[1] !== 'boolean') {
      const msg = 'setRoutinePaused requires (routineId: number, paused: boolean)';
      if (context) {
        context.addMessage(msg, 'danger');
        context.setChunkResultStatus('error');
      }
      throw new Error(msg);
    }
    await firstValueFrom(
      this.executionPlanService.setPlanPaused(projectId, args[0], args[1])
    );
  }

  /**
   * Queue a routine run. Actual execution happens in enoch for scheduled/headless runs.
   * args: [routineId: number, params?] or [routineName: string, params?]
   */
  async triggerRoutine(args: any[], context?: ChunkContext): Promise<ExecJob | null> {
    console.warn('[RoutineService] Routine triggered — will execute in enoch (headless).');
    if (context) {
      context.addMessage(
        'Routine triggered — will execute in enoch (headless executor).',
        'warning'
      );
    }

    const projectId = this.appState.projectId();
    if (!projectId) {
      const msg = 'triggerRoutine: no active project';
      if (context) {
        context.addMessage(msg, 'danger');
      }
      throw new Error(msg);
    }
    if (!args?.length) {
      const msg =
        'triggerRoutine requires (routineId: number, params?) or (routineName: string, params?)';
      if (context) {
        context.addMessage(msg, 'danger');
        context.setChunkResultStatus('error');
      }
      throw new Error(msg);
    }

    const first = args[0];
    let routineId: number;

    if (typeof first === 'number') {
      routineId = first;
    } else if (typeof first === 'string') {
      const plan = await firstValueFrom(
        this.executionPlanService.getPlanByRoutineName(projectId, first.trim()).pipe(
          catchError((err: { status?: number }) => {
            if (err?.status === 404) {
              return of(null);
            }
            throw err;
          })
        )
      );
      if (!plan) {
        const msg = `No routine named "${first.trim()}"`;
        if (context) {
          context.addMessage(msg, 'danger');
        }
        return null;
      }
      routineId = plan.routineId;
    } else {
      const msg = 'triggerRoutine: first argument must be routineId (number) or routine name (string)';
      if (context) {
        context.addMessage(msg, 'danger');
        context.setChunkResultStatus('error');
      }
      throw new Error(msg);
    }

    const body: TriggerRoutineRequest =
      args.length > 1 && args[1] !== null && typeof args[1] === 'object'
        ? (args[1] as TriggerRoutineRequest)
        : {};

    return await firstValueFrom(
      this.executionPlanService.triggerPlan(projectId, routineId, body)
    );
  }

  /**
   * Download an object storage file into the workflow workspace using streaming I/O (SCRUM-333).
   * Requires an active Arrow Flight WebSocket (ARROW_FLIGHT.connect) for presigning.
   *
   * args: [connectionName: string, objectPath: string, workflowFileName: string, durationSeconds?: number]
   */
  async downloadFromStorage(args: any[], context?: ChunkContext): Promise<void> {
    const skipMsg =
      'downloadFromStorage skipped — not running in Pyodide workflow context (file is available during routine execution in enoch)';
    if (!this.pyodideFS) {
      console.warn(`[RoutineService] ${skipMsg}`);
      if (context) {
        context.addMessage(skipMsg, 'warning');
      }
      return;
    }

    if (!Array.isArray(args) || args.length < 3) {
      const msg =
        'downloadFromStorage requires (connectionName: string, objectPath: string, workflowFileName: string, durationSeconds?: number)';
      if (context) {
        context.addMessage(msg, 'danger');
        context.setChunkResultStatus('error');
      }
      throw new Error(msg);
    }

    const nonStringIndex = [0, 1, 2].find((index) => typeof args[index] !== 'string');
    if (nonStringIndex !== undefined) {
      const msg =
        'downloadFromStorage: connection name, object path, and workflow file name must all be strings';
      if (context) {
        context.addMessage(msg, 'danger');
        context.setChunkResultStatus('error');
      }
      throw new Error(msg);
    }

    const connectionName = (args[0] as string).trim();
    const objectPath = (args[1] as string).trim();
    const workflowFileName = (args[2] as string).trim();

    let durationSeconds: number | undefined;
    if (args.length > 3 && args[3] != null && args[3] !== '') {
      const rawDuration = args[3];
      const n =
        typeof rawDuration === 'number'
          ? rawDuration
          : Number(String(rawDuration).trim());
      if (!Number.isFinite(n) || n <= 0) {
        const msg =
          'downloadFromStorage: durationSeconds, when provided, must be a positive number';
        if (context) {
          context.addMessage(msg, 'danger');
          context.setChunkResultStatus('error');
        }
        throw new Error(msg);
      }
      durationSeconds = n;
    }

    if (!connectionName || !objectPath || !workflowFileName) {
      const msg = 'downloadFromStorage: connection name, object path, and workflow file name must be non-empty';
      if (context) {
        context.addMessage(msg, 'danger');
        context.setChunkResultStatus('error');
      }
      throw new Error(msg);
    }

    try {
      // Presigned read (Get) URL via ehq-server's HTTP signed-url endpoint
      // (EHQ-5) — no Arrow Flight session required.
      const presignedUrl = await this.objectStorageFileService.presignUrl([
        connectionName,
        objectPath,
        durationSeconds,
        'read',
      ]);

      if (!presignedUrl || typeof presignedUrl !== 'string') {
        throw new Error('Presign did not return a URL');
      }

      const response = await fetch(presignedUrl, { credentials: 'omit' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText}`);
      }

      const fs = this.pyodideFS;
      const filesDir = `${PYTHON_DIRECTORIES.WORKING_DIR}/${PYTHON_DIRECTORIES.FILES_SUBDIR}`;
      try {
        fs.mkdirTree(filesDir);
      } catch {
        // Directory may already exist
      }
      const safeRelative = RoutineService.sanitizeWorkflowRelativePath(workflowFileName);
      const fullPath = `${filesDir}/${safeRelative}`;

      const totalBytes = await RoutineService.streamHttpBodyToEmscriptenFs(response, fs, fullPath);

      const mb = (totalBytes / (1024 * 1024)).toFixed(1);
      if (context) {
        context.addMessage(
          `Streamed ${mb} MB from storage to ${safeRelative} (chunked download, SCRUM-333)`,
          'success'
        );
      }

      const shouldSyncCache =
        totalBytes > 0 && totalBytes <= MAX_BYTES_FOR_CACHE_SYNC;

      if (shouldSyncCache) {
        await this.syncNotebookCacheFromPyodideFile(
          safeRelative,
          fullPath,
          totalBytes,
          context
        );
      } else if (context && totalBytes > MAX_BYTES_FOR_CACHE_SYNC) {
        context.addMessage(
          'Large download: file is on the Python workflow filesystem; notebook cache sync skipped to save memory.',
          'warning'
        );
      }

      this.messageService.sendMessage(RELOAD_NOTEBOOK_FILES);
    } catch (error: unknown) {
      const msg = `downloadFromStorage failed: ${error instanceof Error ? error.message : String(error)}`;
      if (context) {
        context.addMessage(msg, 'danger');
        context.setChunkResultStatus('error');
      }
      throw new Error(msg);
    }
  }

  /**
   * Sync a downloaded file into notebook cache (small files only).
   */
  private async syncNotebookCacheFromPyodideFile(
    workflowFileName: string,
    fullPath: string,
    byteLength: number,
    context?: ChunkContext
  ): Promise<void> {
    const fs = this.pyodideFS;
    if (!fs || byteLength <= 0) {
      return;
    }

    let data: Uint8Array;
    try {
      data = fs.readFile(fullPath);
    } catch (e) {
      console.warn('[RoutineService] Could not read file for notebook cache sync:', e);
      return;
    }

    const blob = new Blob([data as BlobPart], { type: 'application/octet-stream' });
    const fileName = GeneralHelpers.fileNameFromString(workflowFileName);
    const fileExtension = GeneralHelpers.fileExtensionFromString(workflowFileName);
    const formData = this.fileDataService.prepareFileData(fileName, fileExtension, blob);

    const cacheContent = await firstValueFrom(this.cacheService.getCacheContent());
    const existingFile = GeneralHelpers.findElementInArrayByProp(
      cacheContent,
      'name',
      workflowFileName
    );

    if (existingFile) {
      await firstValueFrom(
        this.cacheService.updateCacheContentFile(existingFile['fileId'], formData)
      );
      if (context) {
        context.addMessage(`Updated notebook cache for ${workflowFileName}`, 'success');
      }
    } else {
      await firstValueFrom(this.cacheService.createCacheContent(formData));
      if (context) {
        context.addMessage(`Added ${workflowFileName} to notebook cache`, 'success');
      }
    }
  }

  private static sanitizeWorkflowRelativePath(name: string): string {
    const trimmed = name.trim().replace(/^[/\\]+/, '');
    const noParent = trimmed.split(/[/\\]/).filter((p) => p && p !== '.' && p !== '..').join('/');
    return noParent || 'download.bin';
  }

  /**
   * Ensures parent directories exist for a file path (Emscripten FS), e.g. files/data/out.json.
   */
  private static ensureParentDirsForEmscriptenFile(fs: any, fullPath: string): void {
    const lastSlash = fullPath.lastIndexOf('/');
    if (lastSlash <= 0) {
      return;
    }
    const parentDir = fullPath.substring(0, lastSlash);
    try {
      fs.mkdirTree(parentDir);
    } catch {
      // Parent directory may already exist
    }
  }

  /**
   * Streams an HTTP response body to an Emscripten FS file without buffering the full body in JS.
   */
  private static async streamHttpBodyToEmscriptenFs(
    response: Response,
    fs: any,
    fullPath: string
  ): Promise<number> {
    RoutineService.ensureParentDirsForEmscriptenFile(fs, fullPath);

    const body = response.body;
    if (!body) {
      const buf = await response.arrayBuffer();
      fs.writeFile(fullPath, new Uint8Array(buf));
      return buf.byteLength;
    }

    const reader = body.getReader();
    let stream: any;
    try {
      stream = fs.open(fullPath, 'w');
    } catch (e) {
      throw new Error(`FS.open failed for ${fullPath}: ${e instanceof Error ? e.message : String(e)}`);
    }

    let total = 0;
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        if (value && value.byteLength) {
          fs.write(stream, value, 0, value.length, total);
          total += value.length;
        }
      }
    } finally {
      try {
        fs.close(stream);
      } catch {
        // ignore
      }
    }

    return total;
  }
}
